import { createSlice } from '@reduxjs/toolkit';
import { DJ_APPS, getAutoSyncPrefs, loadAllAutoSyncPrefs, setAutoSyncPrefsStored } from '../../utils/djAutoSync';

/**
 * Auto-sync preferences per DJ app, mirrored from localStorage (see
 * utils/djAutoSync.js — per-user keys) so the sidebar toggles, the Settings
 * page, the change listener and AutoSyncManager all read one live copy.
 * Mutate via the thunks below — they persist to storage AND update here.
 */
const emptyPrefs = () => Object.fromEntries(DJ_APPS.map(a => [a, { enabled: false, includeNew: false }]));

const djAutoSyncSlice = createSlice({
    name: 'djAutoSync',
    initialState: {
        prefs: emptyPrefs(),
        hydrated: false,
    },
    reducers: {
        hydrateAutoSyncPrefs(state, action) {
            state.prefs = { ...emptyPrefs(), ...(action.payload || {}) };
            state.hydrated = true;
        },
        setAutoSyncPref(state, action) {
            const { app, ...patch } = action.payload;
            state.prefs[app] = { ...state.prefs[app], ...patch };
        },
    },
});

export const { hydrateAutoSyncPrefs, setAutoSyncPref } = djAutoSyncSlice.actions;

/** (Re)load prefs for the current auth bucket — on boot and account switch. */
export const hydrateAutoSyncFromStorage = () => (dispatch, getState) => {
    const userId = getState().auth?.user?.id || null;
    dispatch(hydrateAutoSyncPrefs(loadAllAutoSyncPrefs(userId)));
};

/** Persist + apply a pref change, e.g. updateAutoSyncPref({ app, enabled: true }). */
export const updateAutoSyncPref = ({ app, ...patch }) => (dispatch, getState) => {
    const userId = getState().auth?.user?.id || null;
    const next = { ...getAutoSyncPrefs(app, userId), ...patch };
    setAutoSyncPrefsStored(app, userId, next);
    dispatch(setAutoSyncPref({ app, ...next }));
    return next;
};

export default djAutoSyncSlice.reducer;
